import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Card } from '../../components/ui/Card.js';
import { Spinner } from '../../components/ui/Spinner.js';
import {
  CARTRACK_KEYS,
  type FleetImmobiliserState,
  cartrackApi,
  cartrackProblem,
} from '../../services/cartrack.js';
import { mytClock, mytDayLabel } from '../../utils/myt.js';

/**
 * 420 (mock-up section 3): every Cartrack vehicle's immobiliser state, from `GET /cartrack/immobilisers`. The whole fleet
 * is read once and filtered on screen; the counts are the platform's. When Cartrack's fleet listing fails the platform
 * reads one car at a time, and the section says so.
 */
const FILTERS: ReadonlyArray<{ key: 'all' | FleetImmobiliserState; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'immobilised', label: 'Immobilised' },
  { key: 'released', label: 'Released' },
  { key: 'unknown', label: 'Unknown' },
  { key: 'not_available', label: 'No immobiliser' },
];
const STATE_LABEL: Record<FleetImmobiliserState, string> = {
  immobilised: 'Immobilised',
  released: 'Released',
  unknown: 'Unknown',
  not_available: 'No immobiliser',
};
const STATE_TONE: Record<FleetImmobiliserState, string> = {
  immobilised: 'bg-red-50 text-red-700',
  released: 'bg-semantic-success-bg text-semantic-success-text',
  unknown: 'bg-surface text-steel',
  not_available: 'bg-surface text-steel',
};
const TH = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-steel';
const TD = 'px-3 py-2 text-left text-sm align-top';
const when = (iso: string) => `${mytDayLabel(iso)}, ${mytClock(iso)}`;

export function ImmobilisersSection() {
  const [filter, setFilter] = useState<'all' | FleetImmobiliserState>('all');
  const fleet = useQuery({
    queryKey: CARTRACK_KEYS.immobilisers,
    queryFn: cartrackApi.immobilisers,
  });
  const data = Array.isArray(fleet.data?.items) ? fleet.data : undefined;
  const shown = data ? data.items.filter((r) => filter === 'all' || r.state === filter) : [];

  return (
    <Card>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink">Immobilisers</h2>
        {data && (
          <span
            data-testid="immobiliser-read-via"
            data-tone={data.read_via === 'per_vehicle' ? 'warn' : 'plain'}
            className={`text-xs ${data.read_via === 'per_vehicle' ? 'text-amber-800' : 'text-steel'}`}
          >
            {data.read_via === 'per_vehicle'
              ? "Read one car at a time: Cartrack's fleet listing is not answering"
              : "Read from Cartrack's fleet listing"}{' '}
            · {mytClock(data.fetched_at)}
          </span>
        )}
      </div>
      {fleet.isPending && <Spinner />}
      {fleet.isError && (
        <p
          data-testid="immobiliser-problem"
          className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {cartrackProblem(fleet.error)?.text ??
            'Could not load the immobilisers. Try again later.'}
        </p>
      )}
      {data && (
        <>
          <div data-testid="immobiliser-filters" className="mb-3 flex flex-wrap gap-2 text-xs">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                aria-pressed={filter === f.key}
                onClick={() => setFilter(f.key)}
                className={`rounded-full border px-3 py-1 ${
                  filter === f.key
                    ? 'border-primary bg-primary text-on-primary'
                    : 'border-hairline text-steel'
                }`}
              >
                {f.label} {data.counts[f.key] ?? 0}
              </button>
            ))}
          </div>
          <table data-testid="immobiliser-rows" className="w-full">
            <thead>
              <tr className="border-b border-hairline">
                <th className={TH}>Cartrack vehicle</th>
                <th className={TH}>CARii car</th>
                <th className={TH}>Immobiliser</th>
                <th className={TH}>CARii command</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.vehicle_id} className="border-b border-hairline/60">
                  <td className={`${TD} font-semibold`}>
                    {r.registration ?? '—'}
                    {r.make_model && (
                      <span className="block text-xs font-normal text-steel">{r.make_model}</span>
                    )}
                  </td>
                  <td className={TD}>
                    {r.car ? (
                      <Link to={`/cars/${r.car.id}/edit`} className="text-primary">
                        {r.car.plate_number}
                      </Link>
                    ) : (
                      <span className="text-steel">Not linked</span>
                    )}
                  </td>
                  <td className={TD}>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATE_TONE[r.state]}`}
                    >
                      {STATE_LABEL[r.state]}
                    </span>
                  </td>
                  <td className={TD}>
                    {r.waiting ? (
                      <>
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                          {r.waiting.action === 'immobilise' ? 'Immobilise' : 'Release'} sent —
                          waiting
                        </span>
                        <span className="block text-xs text-steel">
                          {when(r.waiting.requested_at)}
                        </span>
                      </>
                    ) : (
                      <span className="text-steel">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {shown.length === 0 && (
            <p data-testid="immobiliser-empty" className="mt-3 text-sm text-steel">
              No car is in this state.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
